import React, { ReactNode } from 'react';
import { SailingDetails, SimpleSailing, BareSimpleSailing } from '@go-shared/types/sailing';

import { getSailingDetails, getSailing, getSimpleSailingsByProperty } from '../lib/api/itinerary';
import { addDaysToUTCDate, isoDate, isoLocalDate, parseDate } from '@go-shared/lib/time';

export interface SailingProviderProps {
  propertyId: string;
  children: (props: SailingProviderChildrenProps) => ReactNode;
  year: number;
  daysBefore: number;
  daysAfter: number;
  sailingPopulator: (bareSailing: BareSimpleSailing) => SimpleSailing;
  date?: string;
  onChangeDate: (date: string) => void;
  onNewSailings?: (sailings: SimpleSailing[]) => void;
  onAddDaysBefore?: (amt: number) => void;
  onAddDaysAfter?: (amt: number) => void;
}

export interface SailingProviderState {
  loading: boolean;
  sailings: SimpleSailing[];
  sailingDetails: SailingDetails[];
}

export interface SailingProviderChildrenProps {
  loading: boolean;
  sailings: SimpleSailing[];
  sailingDetails: SailingDetails[];
  selectedMonth: number;
  selectedYear: number;
  selectedDate: string;
  addDaysBefore: (amt?: number) => void;
  addDaysAfter: (amt?: number) => void;
  changeDate: (isoDate: string) => void;
  changeMonth: (month: number) => void;
  changeYear: (year: number) => void;
  updateSailing: (sailingId: string) => Promise<void>;
  reload: () => Promise<void>;
}

const ADD_DAYS_BUTTON = 15;

export default class SailingProvider extends React.Component<
  SailingProviderProps,
  SailingProviderState
> {
  public readonly state: SailingProviderState = {
    loading: false,
    sailings: [],
    sailingDetails: [],
  };

  private selectedMonth: number = new Date().getMonth();
  private selectedYear: number = new Date().getFullYear();
  private selectedDate = isoLocalDate(new Date());

  constructor(props: SailingProviderProps) {
    super(props);
    if (props.date) this.selectedDate = props.date;
  }

  public componentDidMount() {
    return this.loadSailings();
  }

  public componentDidUpdate(prevProps: SailingProviderProps) {
    if (
      prevProps.propertyId !== this.props.propertyId ||
      prevProps.daysBefore !== this.props.daysBefore ||
      prevProps.daysAfter !== this.props.daysAfter
    ) {
      return this.loadSailings();
    }
  }

  public render() {
    return (
      <>
        {this.props.children({
          loading: this.state.loading,
          sailings: this.state.sailings,
          sailingDetails: this.state.sailingDetails,
          selectedMonth: this.selectedMonth,
          selectedYear: this.selectedYear,
          selectedDate: this.selectedDate,
          addDaysBefore: this.handleAddDaysBefore,
          addDaysAfter: this.handleAddDaysAfter,
          changeDate: this.handleDateChange,
          changeMonth: this.handleMonthChange,
          changeYear: this.handleYearChange,
          updateSailing: this.handleSailingUpdate,
          reload: this.handleReload,
        })}
      </>
    );
  }

  private async loadSailings() {
    this.setState({ loading: true });

    try {
      const sailings = await this.loadShipSailings();
      if (this.props.onNewSailings) {
        this.props.onNewSailings(sailings);
      }
    } finally {
      this.setState({ loading: false });
    }
  }

  private async loadShipSailings() {
    let sailingsFromDate = new Date(this.selectedDate);
    let sailingsToDate = new Date(this.selectedDate);

    const parsedSelectedDate = parseDate(this.selectedDate);

    if (parsedSelectedDate) {
      sailingsFromDate = addDaysToUTCDate(parsedSelectedDate, -this.props.daysBefore);
      sailingsToDate = addDaysToUTCDate(parsedSelectedDate, this.props.daysAfter);
    }

    let [bareSailings, sailingDetails] = await Promise.all([
      getSimpleSailingsByProperty(
        this.props.propertyId,
        isoDate(sailingsFromDate),
        isoDate(sailingsToDate)
      ),
      getSailingDetails(this.props.propertyId, sailingsFromDate, sailingsToDate),
    ]);

    const sailings = bareSailings.map(this.props.sailingPopulator);

    this.setState({ sailings, sailingDetails });

    return sailings;
  }

  private handleAddDaysBefore = (amt: number = ADD_DAYS_BUTTON) => {
    if (this.props.onAddDaysBefore) this.props.onAddDaysBefore(amt);
  };

  private handleAddDaysAfter = (amt: number = ADD_DAYS_BUTTON) => {
    if (this.props.onAddDaysAfter) this.props.onAddDaysAfter(amt);
  };

  private handleDateChange = (date: string) => {
    this.selectedDate = date;
    if (this.props.onChangeDate) this.props.onChangeDate(date);
    return this.loadSailings();
  };

  private handleMonthChange = (newMonth: number) => {
    this.selectedMonth = newMonth;
    return this.loadSailings();
  };

  private handleYearChange = (newYear: number) => {
    this.selectedYear = newYear;
    this.selectedMonth = 0;
    return this.loadSailings();
  };

  private handleReload = () => {
    return this.loadSailings();
  };

  /**
   * This function is an optimization when we just want to reload a single sailing rather than all
   * sailings. The id of the sailing to update is passed as a parameter, which triggers a render
   * with all sailings including the update
   *
   */
  private handleSailingUpdate = async (sailingId: string) => {
    const bareSailingUpdated = await getSailing(sailingId);
    const sailingUpdated = this.props.sailingPopulator(bareSailingUpdated);

    let match = this.state.sailings.findIndex(sailing => sailing._id === sailingId);
    if (match < 0) return;

    const newSailings = [...this.state.sailings];
    newSailings.splice(match, 1, sailingUpdated);

    this.setState({ sailings: newSailings });
    if (this.props.onNewSailings) this.props.onNewSailings(newSailings);
  };
}
